// NewRelic Browser Logs HTTP sink.
//
// Ships log batches directly to https://log-api.newrelic.com/log/v1 (or the
// configured regional endpoint). Uses navigator.sendBeacon on page unload so
// the final batch survives the navigation; falls back to fetch keepalive
// otherwise.
//
// Activated when REACT_APP_LOG_TRANSPORT includes 'nr' AND an ingest key is
// configured. Without a key, the sink no-ops (so you can flip the env var
// after vendor onboarding without code changes).

import { logEnv } from '../env';
import type { LogRecord, Sink } from '../transport';

interface NewRelicPayload {
  common: { attributes: Record<string, unknown> };
  logs: Array<Record<string, unknown>>;
}

function buildPayload(batch: LogRecord[]): NewRelicPayload {
  return {
    common: {
      attributes: {
        service: 'zopeo-frontend',
        env: logEnv.env,
        appVersion: logEnv.appVersion,
        buildSha: logEnv.buildSha,
      },
    },
    logs: batch.map((r) => {
      // NewRelic expects 'timestamp' (ms epoch) and 'message' at minimum.
      // Everything else becomes searchable attributes.
      const ts = typeof r.timestamp === 'string' ? Date.parse(r.timestamp) : Date.now();
      return {
        ...r,
        timestamp: Number.isFinite(ts) ? ts : Date.now(),
        message: typeof r.message === 'string' ? r.message : String(r.event ?? 'log'),
      };
    }),
  };
}

function postWithFetch(body: string, key: string, endpoint: string): void {
  // keepalive lets the request survive page transitions (limited to 64 KB).
  try {
    void fetch(endpoint, {
      method: 'POST',
      keepalive: true,
      headers: {
        'Content-Type': 'application/json',
        'Api-Key': key,
      },
      body,
    }).catch(() => {
      // Sink must never bubble errors into host app.
    });
  } catch {
    // Synchronous fetch error (CSP, etc.) — drop the batch.
  }
}

function postWithBeacon(body: string, endpoint: string, key: string): boolean {
  if (typeof navigator === 'undefined' || !navigator.sendBeacon) return false;
  try {
    // sendBeacon cannot set custom headers, so we encode the key in the URL.
    // NewRelic accepts ?Api-Key=… as an alternative to the header.
    const url = `${endpoint}${endpoint.includes('?') ? '&' : '?'}Api-Key=${encodeURIComponent(key)}`;
    const blob = new Blob([body], { type: 'application/json' });
    return navigator.sendBeacon(url, blob);
  } catch {
    return false;
  }
}

let unloadFlushMode = false;
export function setUnloadFlushMode(on: boolean): void {
  unloadFlushMode = on;
}

export function createNewRelicSink(): Sink {
  return (batch: LogRecord[]) => {
    if (batch.length === 0) return;
    const key = logEnv.nrIngestKey;
    if (!key) return; // no key configured → no-op
    const endpoint = logEnv.nrLogEndpoint;
    const body = JSON.stringify([buildPayload(batch)]);
    if (unloadFlushMode) {
      const ok = postWithBeacon(body, endpoint, key);
      if (!ok) postWithFetch(body, key, endpoint);
      return;
    }
    postWithFetch(body, key, endpoint);
  };
}
